import React, { useState, useCallback, useContext, useEffect, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Hidden from "@material-ui/core/Hidden";
import { makeStyles, useTheme, useMediaQuery } from "@material-ui/core/styles";
import ChatBubbleOutlineRoundedIcon from "@material-ui/icons/ChatBubbleOutlineRounded";
import FlashOnRoundedIcon from "@material-ui/icons/FlashOnRounded";
import EmojiEmotionsOutlinedIcon from "@material-ui/icons/EmojiEmotionsOutlined";
import TicketsManager from "../../components/TicketsManagerTabs";
import Ticket from "../../components/Ticket";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import useAuth from "../../hooks/useAuth";

const defaultTicketsManagerWidth = 550;
// Bumped min from 324 → 460. Below ~460px the filter row
// (Queues/Connections/Users/Tags) and the segmented control wrap onto
// multiple lines and the floating count badge collides with rows above.
// 460 is the narrowest the panel can shrink to without breaking layout.
const minTicketsManagerWidth = 460;
const maxTicketsManagerWidth = 1200;

const useStyles = makeStyles((theme) => ({
	chatContainer: {
		flex: 1,
		padding: "12px",
		height: `calc(100% - 48px)`,
		overflowY: "hidden",
		position: "relative",
		background:
			theme.palette.type === "light"
				? "linear-gradient(180deg, #f6faf7 0%, #eef5f0 100%)"
				: "#0B0D0C",
		"&::before": {
			content: '""',
			position: "absolute",
			inset: 0,
			pointerEvents: "none",
			background:
				theme.palette.type === "light"
					? "radial-gradient(ellipse 700px 320px at 50% 0%, rgba(37, 211, 102, 0.14), transparent 70%)"
					: "radial-gradient(ellipse 700px 320px at 50% 0%, rgba(37, 211, 102, 0.10), transparent 70%)",
		},
		"& > *": { position: "relative" },
		[theme.breakpoints.down("sm")]: { padding: 0 },
	},
	chatPapper: {
		display: "flex",
		height: "100%",
		gap: 12,
		[theme.breakpoints.down("sm")]: { gap: 0 },
	},
	contactsWrapper: {
		display: "flex",
		height: "100%",
		flexDirection: "column",
		overflowY: "hidden",
		position: "relative",
		flexShrink: 0,
		borderRadius: 16,
		background: theme.palette.type === "light" ? "#fff" : "#171717",
		border: theme.palette.type === "light"
			? "1px solid #eef0f5"
			: "1px solid rgba(255,255,255,0.06)",
		boxShadow: theme.palette.type === "light"
			? "0 4px 18px rgba(15, 23, 42, 0.04)"
			: "0 4px 18px rgba(0,0,0,0.35)",
		overflow: "hidden",
		[theme.breakpoints.down("sm")]: {
			width: "100% !important",
			borderRadius: 0,
			border: "none",
			boxShadow: "none",
		},
	},
	contactsWrapperMobileHidden: {
		[theme.breakpoints.down("sm")]: {
			display: "none",
		},
	},
	messagesWrapper: {
		display: "flex",
		height: "100%",
		flexDirection: "column",
		flexGrow: 1,
		minWidth: 0,
		overflow: "hidden",
		borderRadius: 16,
		background: theme.palette.type === "light" ? "#fff" : "#171717",
		border: theme.palette.type === "light"
			? "1px solid #eef0f5"
			: "1px solid rgba(255,255,255,0.06)",
		boxShadow: theme.palette.type === "light"
			? "0 4px 18px rgba(15, 23, 42, 0.04)"
			: "0 4px 18px rgba(0,0,0,0.35)",
		[theme.breakpoints.down("sm")]: {
			position: "absolute",
			top: 0,
			left: 0,
			right: 0,
			bottom: 0,
			zIndex: 10,
			backgroundColor: theme.palette.background.default,
			borderRadius: 0,
			border: "none",
			boxShadow: "none",
		},
	},
	messagesWrapperMobileHidden: {
		[theme.breakpoints.down("sm")]: {
			display: "none",
		},
	},
	welcomeMsg: {
		background: "transparent",
		border: "none",
		boxShadow: "none",
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
		alignItems: "center",
		height: "100%",
		padding: "48px 32px",
		boxSizing: "border-box",
		[theme.breakpoints.down("md")]: { padding: "32px 20px" },
	},
	welcomeStage: {
		position: "relative",
		width: "100%",
		maxWidth: 720,
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		gap: 28,
	},
	welcomeBubbles: {
		position: "absolute",
		inset: -40,
		pointerEvents: "none",
		zIndex: 0,
		"& span": {
			position: "absolute",
			borderRadius: 18,
			background: theme.palette.type === "light"
				? "linear-gradient(135deg, rgba(37, 211, 102, 0.12), rgba(18, 140, 126, 0.06))"
				: "linear-gradient(135deg, rgba(37, 211, 102, 0.18), rgba(18, 140, 126, 0.08))",
		},
		"& span:nth-child(1)": { width: 96, height: 64, top: 8, left: "8%", transform: "rotate(-6deg)" },
		"& span:nth-child(2)": { width: 72, height: 48, bottom: 32, left: "12%", transform: "rotate(10deg)" },
		"& span:nth-child(3)": { width: 110, height: 70, top: 24, right: "10%", transform: "rotate(8deg)" },
		"& span:nth-child(4)": { width: 80, height: 52, bottom: 12, right: "14%", transform: "rotate(-12deg)" },
	},
	// Premium hero recipe (Resend / Linear marketing / Vercel
	// dashboard top card):
	//   * dark surface base, NOT a brand-color fill — solid brand
	//     fills destroy the dark feel
	//   * two radial brand glows BEHIND the content (top-left
	//     primary green, bottom-right deep teal)
	//   * inset 0 1px 0 rgba(255,255,255,0.06) for the catching-
	//     light bevel
	//   * 1px hairline border keeps the card legible against the
	//     atmospheric page bg
	// Light theme stays on the airy brand gradient — it works
	// there because the surrounding page is white.
	welcomeHero: {
		position: "relative",
		zIndex: 1,
		width: "100%",
		borderRadius: 24,
		padding: "32px 36px",
		background: theme.palette.type === "light"
			? "linear-gradient(135deg, #25d366 0%, #128c7e 100%)"
			: `radial-gradient(800px circle at 20% 0%, rgba(46, 227, 122, 0.14), transparent 50%),
			   radial-gradient(600px circle at 90% 110%, rgba(18, 140, 126, 0.12), transparent 50%),
			   linear-gradient(180deg, #131614 0%, #0c0d0f 100%)`,
		color: "#fff",
		border: theme.palette.type === "light"
			? "none"
			: "1px solid rgba(255, 255, 255, 0.08)",
		boxShadow: theme.palette.type === "light"
			? "0 18px 48px rgba(37, 211, 102, 0.35)"
			: "inset 0 1px 0 0 rgba(255, 255, 255, 0.06)",
		overflow: "hidden",
		"&::before": {
			content: '""',
			position: "absolute",
			inset: 0,
			pointerEvents: "none",
			background: theme.palette.type === "light"
				? "radial-gradient(ellipse 500px 200px at 100% 0%, rgba(255,255,255,0.22), transparent 60%)"
				: "none",
		},
		"& > *": { position: "relative" },
		[theme.breakpoints.down("sm")]: { padding: "24px 22px" },
	},
	welcomeHeroLogo: {
		width: 56,
		height: 56,
		borderRadius: 16,
		// On dark: brand-tinted glass-style chip with brand glow.
		// On light: keep the soft translucent white.
		background: theme.palette.type === "light"
			? "rgba(255,255,255,0.18)"
			: "linear-gradient(135deg, rgba(46, 227, 122, 0.18), rgba(18, 140, 126, 0.12))",
		boxShadow: theme.palette.type === "light"
			? "none"
			: "inset 0 1px 0 rgba(255,255,255,0.08), 0 0 24px -4px rgba(46, 227, 122, 0.35)",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		marginBottom: 16,
		"& img": { maxWidth: 38, maxHeight: 38, filter: "brightness(0) invert(1)" },
	},
	welcomeTitle: {
		fontSize: 30,
		fontWeight: 800,
		letterSpacing: -0.6,
		lineHeight: 1.15,
		marginBottom: 8,
		// Gradient text on dark — Vercel/Resend hero pattern. The
		// title fades from pure white to a soft gray so it has depth
		// against the dark hero bg without needing decoration.
		...(theme.palette.type === "light"
			? {}
			: {
				background: "linear-gradient(180deg, #ffffff 0%, #a8acb3 110%)",
				WebkitBackgroundClip: "text",
				WebkitTextFillColor: "transparent",
				backgroundClip: "text",
			}),
		[theme.breakpoints.down("sm")]: { fontSize: 22 },
	},
	welcomeSubtitle: {
		fontSize: 14,
		fontWeight: 500,
		opacity: 0.92,
		lineHeight: 1.5,
		maxWidth: 460,
	},
	welcomeTips: {
		position: "relative",
		zIndex: 1,
		width: "100%",
		display: "grid",
		gridTemplateColumns: "repeat(3, 1fr)",
		gap: 14,
		[theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
	},
	tipCard: {
		padding: "18px 18px 16px",
		borderRadius: 14,
		background: theme.palette.type === "light" ? "#fff" : "#131614",
		border: theme.palette.type === "light"
			? "1px solid #eef0f5"
			: "1px solid rgba(255,255,255,0.07)",
		boxShadow: theme.palette.type === "light"
			? "0 4px 18px rgba(15, 23, 42, 0.04)"
			: "inset 0 1px 0 0 rgba(255,255,255,0.04)",
		transition: "border-color .18s ease, box-shadow .18s ease",
		"&:hover": {
			borderColor: theme.palette.type === "light"
				? "#86efac"
				: "rgba(255,255,255,0.14)",
			boxShadow: theme.palette.type === "light"
				? "0 10px 24px rgba(37, 211, 102, 0.14)"
				: `inset 0 1px 0 0 rgba(255,255,255,0.06),
				   0 1px 2px rgba(0, 0, 0, 0.4),
				   0 8px 24px -8px rgba(0, 0, 0, 0.55)`,
		},
	},
	tipIconBox: {
		width: 36,
		height: 36,
		borderRadius: 10,
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		background: theme.palette.type === "light"
			? "linear-gradient(135deg, #dcfce7 0%, #d1fae5 100%)"
			: "rgba(37, 211, 102, 0.14)",
		marginBottom: 12,
		"& svg": {
			fontSize: 20,
			color: theme.palette.type === "light" ? "#128c7e" : "#86efac",
		},
	},
	tipTitle: {
		fontSize: 13,
		fontWeight: 700,
		color: theme.palette.type === "light" ? "#0f172a" : "#f1f5f9",
		marginBottom: 4,
	},
	tipText: {
		fontSize: 12.5,
		fontWeight: 500,
		lineHeight: 1.5,
		color: theme.palette.type === "light" ? "#64748b" : "#94a3b8",
	},
	dragger: {
		// Resize handle stays — user wants the panel resizable, just
		// with a sensible floor (minTicketsManagerWidth = 460). Below
		// that the segmented control wraps and the layout breaks.
		width: "6px",
		cursor: "ew-resize",
		position: "absolute",
		top: 8,
		bottom: 8,
		left: -3,
		zIndex: 1000,
		borderRadius: 6,
		backgroundColor: "transparent",
		userSelect: "none",
		transition: "background-color 0.2s",
		"&:hover": {
			backgroundColor: "rgba(37, 211, 102, 0.45)",
		},
		[theme.breakpoints.down("sm")]: {
			display: "none",
		},
	},
	logo: {
		maxWidth: 120,
		logo: theme.logo,
		content: "url(" + (theme.mode === "light" ? theme.calculatedLogoLight() : theme.calculatedLogoDark()) + ")"
	},
}));

const TicketsCustom = () => {
	const { user } = useContext(AuthContext);
	const { getCurrentUserInfo } = useAuth();
	const { t } = useTranslation();
	const [currentUser, setCurrentUser] = useState({});
	// Clamp the user's saved width to the new floor (460). Users who
	// had previously dragged the panel below the new minimum would
	// otherwise re-mount at the old too-small value and the layout
	// would still look broken.
	const clampWidth = (w) => {
		const n = Number(w) || defaultTicketsManagerWidth;
		if (n < minTicketsManagerWidth) return defaultTicketsManagerWidth;
		if (n > maxTicketsManagerWidth) return maxTicketsManagerWidth;
		return n;
	};

	const classes = useStyles({ ticketsManagerWidth: clampWidth(user.defaultTicketsManagerWidth) });
	const { ticketId } = useParams();

    const [ticketsManagerWidth, setTicketsManagerWidth] = useState(clampWidth(user?.defaultTicketsManagerWidth));
	const ticketsManagerWidthRef = useRef(ticketsManagerWidth);
	const containerRef = useRef(null);
	const messagesRef = useRef(null);

	useEffect(() => {
		if (user && user.defaultTicketsManagerWidth) {
			getCurrentUserInfo().then(
				(u) => {
				  setCurrentUser(u);
				  setTicketsManagerWidth(clampWidth(u.defaultTicketsManagerWidth));
				}
			  );
		}
	}, [user]);

	const handleMouseDown = () => {
		document.addEventListener("mouseup", handleMouseUp, true);
		document.addEventListener("mousemove", handleMouseMove, true);
	};

	const handleSaveContact = async (value) => {
		if (value < minTicketsManagerWidth) value = minTicketsManagerWidth;
		await api.put(`/users/toggleChangeWidht/${user.id}`, { defaultTicketsManagerWidth: value });
	};

	const handleMouseMove = useCallback(
		(e) => {
		  if (!containerRef.current || !messagesRef.current) return;
	  
		  const totalWidth = document.body.clientWidth;
	  
		  const cursorPositionFromRight = totalWidth - e.clientX;
	  
		  const messagesWidth = messagesRef.current.offsetWidth;
	  
		  const navbarWidth = totalWidth - containerRef.current.offsetWidth - messagesWidth;
	  
		  const newWidth = cursorPositionFromRight - navbarWidth;
	  
		  if (newWidth > minTicketsManagerWidth && newWidth < maxTicketsManagerWidth) {
			if (newWidth !== ticketsManagerWidthRef.current) {
			  ticketsManagerWidthRef.current = newWidth;
			  setTicketsManagerWidth(newWidth);
			}
		  }
		},
		[minTicketsManagerWidth, maxTicketsManagerWidth]
	  );
	

	const handleMouseUp = async () => {
		document.removeEventListener("mouseup", handleMouseUp, true);
		document.removeEventListener("mousemove", handleMouseMove, true);

		const newWidth = ticketsManagerWidthRef.current;

		if (newWidth !== ticketsManagerWidth) {
			await handleSaveContact(newWidth);
		}
	};

	return (
		<div className={classes.chatContainer}>
			<div className={classes.chatPapper}>
				{/* Ticket list - hide on mobile when ticket is selected */}
				<div
					className={`${classes.contactsWrapper} ${ticketId ? classes.contactsWrapperMobileHidden : ""}`}
					style={{ width: ticketsManagerWidth }}
					ref={containerRef}
				>
					<TicketsManager />
					<div onMouseDown={handleMouseDown} className={classes.dragger} />
				</div>
				{/* Messages area - show full screen on mobile when ticket selected */}
				<div 
					ref={messagesRef} 
					className={`${classes.messagesWrapper} ${!ticketId ? classes.messagesWrapperMobileHidden : ""}`}
				>
					{ticketId ? (
						<Ticket />
					) : (
						<Hidden only={["sm", "xs"]}>
							<div className={classes.welcomeMsg}>
								<div className={classes.welcomeStage}>
									<div className={classes.welcomeBubbles} aria-hidden>
										<span /><span /><span /><span />
									</div>
									<div className={classes.welcomeHero}>
										<div className={classes.welcomeHeroLogo}>
											{/* Was an <img> driven by `content: url(theme.calculatedLogo*())`
											    which silently rendered the alt text "logo" when the
											    whitelabel logo URL was missing/blocked on staging.
											    Brand chat icon always works and matches the surrounding
											    welcomeHeroLogo glass-chip styling. */}
											<ChatBubbleOutlineRoundedIcon style={{ fontSize: 32, color: "#fff" }} />
										</div>
										<div className={classes.welcomeTitle}>
											{user?.name
												? `${t("chat.welcomeBack", { defaultValue: "ברוך שובך" })}, ${user.name} 👋`
												: `${t("chat.welcomeBack", { defaultValue: "ברוך שובך" })} 👋`}
										</div>
										<div className={classes.welcomeSubtitle}>
											{t("chat.noTicketMessage")}
										</div>
									</div>
									<div className={classes.welcomeTips}>
										<div className={classes.tipCard}>
											<span className={classes.tipIconBox}>
												<ChatBubbleOutlineRoundedIcon />
											</span>
											<div className={classes.tipTitle}>
												{t("chat.tip1Title", { defaultValue: "בחר שיחה" })}
											</div>
											<div className={classes.tipText}>
												{t("chat.tip1Text", { defaultValue: "לחץ על כרטיס שיחה ברשימה כדי להתחיל לענות לפניות" })}
											</div>
										</div>
										<div className={classes.tipCard}>
											<span className={classes.tipIconBox}>
												<FlashOnRoundedIcon />
											</span>
											<div className={classes.tipTitle}>
												{t("chat.tip2Title", { defaultValue: "תגובות מהירות" })}
											</div>
											<div className={classes.tipText}>
												{t("chat.tip2Text", { defaultValue: "השתמש בתגובות מוכנות מראש כדי לחסוך זמן בכל פניה" })}
											</div>
										</div>
										<div className={classes.tipCard}>
											<span className={classes.tipIconBox}>
												<EmojiEmotionsOutlinedIcon />
											</span>
											<div className={classes.tipTitle}>
												{t("chat.tip3Title", { defaultValue: "תהיה הראשון להגיב" })}
											</div>
											<div className={classes.tipText}>
												{t("chat.tip3Text", { defaultValue: "תגובה תוך 7 דקות מגדילה את שביעות הרצון של הלקוח ב-32%" })}
											</div>
										</div>
									</div>
								</div>
							</div>
						</Hidden>
					)}
				</div>
			</div>
		</div>
	);
};

export default TicketsCustom;